/**
 * BillingView Component
 * Admin-only view for managing billing, subscriptions, and invoices
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip
} from '@mui/material';
import {
  DownloadOutlined as DownloadIcon,
  AddOutlined as AddIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  MoreVertOutlined as MoreVertIcon,
  CheckCircleOutlined as CheckCircleIcon,
  InfoOutlined as InfoIcon,
  CreditCardOutlined as CreditCardIcon,
  ReceiptOutlined as ReceiptIcon,
  BusinessOutlined as BusinessIcon,
  CloseOutlined as CloseIcon
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import { useToast } from '@/components/Toast';
import {
  getBillingData,
  downloadInvoice,
  updateBillingAddress,
  BillingData,
  Invoice,
  PaymentMethod,
  BillingAddress
} from '@/services/billingService';
import { format } from 'date-fns';
import { SupportChat } from '@/components/SupportChat';
import { ManageSeatsDialog } from '@/components/ManageSeatsDialog';
import { StorageUsageDialog } from '@/components/StorageUsageDialog';
import { TokenUsageDialog } from '@/components/TokenUsageDialog';
import { AddPaymentDialog, PaymentMethodData } from '@/components/AddPaymentDialog';
import { useNavigate } from 'react-router-dom';

interface BillingViewProps {
  companyId: string;
}

export const BillingView: React.FC<BillingViewProps> = ({ companyId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser } = useAppUser();
  const toast = useToast();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [billingData, setBillingData] = useState<BillingData | null>(null);
  const [isAddressDialogOpen, setIsAddressDialogOpen] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [editedAddress, setEditedAddress] = useState<BillingAddress | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [manageSeatsDialogOpen, setManageSeatsDialogOpen] = useState(false);
  const [storageDialogOpen, setStorageDialogOpen] = useState(false);
  const [tokenUsageDialogOpen, setTokenUsageDialogOpen] = useState(false);
  const [addPaymentDialogOpen, setAddPaymentDialogOpen] = useState(false);

  // Load billing data
  useEffect(() => {
    if (appUser?.id && companyId) {
      loadBillingData();
    }
  }, [appUser?.id, companyId]);

  const loadBillingData = async () => {
    if (!appUser?.id) return;

    try {
      setIsLoading(true);
      const data = await getBillingData(
        companyId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setBillingData(data);
    } catch (error) {
      console.error('Error loading billing data:', error);
      toast.showError('Failed to load billing data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoice: Invoice) => {
    if (!appUser?.id) return;

    try {
      const success = await downloadInvoice(
        invoice.id,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      if (success) {
        toast.showSuccess('Invoice downloaded successfully');
      } else {
        toast.showError('Failed to download invoice');
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.showError('Failed to download invoice');
    }
  };

  const handleEditAddress = () => {
    if (billingData?.billingAddress) {
      setEditedAddress({ ...billingData.billingAddress });
      setIsAddressDialogOpen(true);
    }
  };

  const handleSaveAddress = async () => {
    if (!appUser?.id || !editedAddress) return;

    try {
      setIsSavingAddress(true);
      const result = await updateBillingAddress(
        companyId,
        appUser.id,
        editedAddress,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      if (result.success) {
        toast.showSuccess('Billing address updated successfully');
        setIsAddressDialogOpen(false);
        loadBillingData();
      } else {
        toast.showError(result.message || 'Failed to update billing address');
      }
    } catch (error) {
      console.error('Error updating billing address:', error);
      toast.showError('Failed to update billing address');
    } finally {
      setIsSavingAddress(false);
    }
  };

  const calculatePercentage = (used: number, total: number) => {
    if (total === 0) return 0;
    return (used / total) * 100;
  };

  const handleConfirmSeats = async (newSeats: number) => {
    // TODO: Implement API call to update seats
    toast.showSuccess(`Seats updated to ${newSeats}`);
    // Reload billing data
    await loadBillingData();
  };

  const handleUpgradePlan = () => {
    // Navigate to plans tab
    navigate('/organization?tab=plans');
  };

  const handleAddPaymentMethod = async (paymentData: PaymentMethodData) => {
    // TODO: Implement API call to add payment method
    toast.showSuccess('Payment method added successfully');
    // Reload billing data
    await loadBillingData();
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!billingData) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">
          No billing data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        p: isMobile ? 2 : 4
      }}
    >
      <Box sx={{ maxWidth: 900, width: '100%', textAlign: 'left' }}>
        {/* Page Header */}
        <Box sx={{ mb: 4, textAlign: 'left' }}>
          <Typography variant="h4" fontWeight={600} gutterBottom sx={{ fontSize: '28px', textAlign: 'left' }}>
            Billing
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '14px', textAlign: 'left' }}>
            Explore plans and manage your subscription, usage, and billing information. Need help?{' '}
            <Typography
              component="span"
              sx={{
                fontSize: '14px',
                color: 'text.secondary',
                textDecoration: 'underline',
                textDecorationColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                textUnderlineOffset: '3px',
                cursor: 'pointer',
                '&:hover': {
                  color: 'text.primary',
                  textDecorationColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                }
              }}
              onClick={() => setChatOpen(true)}
            >
              Message us →
            </Typography>
          </Typography>
        </Box>

        {/* Current Plan */}
        <Box sx={{ mb: 4, textAlign: 'left' }}>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ fontSize: '16px', textAlign: 'left' }}>
            Current plan
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '14px', textAlign: 'left' }}>
            Renews {format(new Date(billingData.plan.renewalDate), 'MMMM do, yyyy')}
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              borderRadius: '8px',
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: 'transparent'
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '13px' }}>
              {billingData.plan.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
              ${billingData.plan.price} per seat, billed {billingData.plan.billingCycle}
            </Typography>
          </Paper>
        </Box>

        {/* Usage Section */}
        <Box sx={{ mb: 4, textAlign: 'left' }}>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ fontSize: '16px', textAlign: 'left' }}>
            Usage
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '14px', textAlign: 'left' }}>
            Manage your seats and credits
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: 2
            }}
          >
            {/* Seats Card */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: '8px',
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: 'transparent'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <BusinessIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '13px' }}>
                  Seats
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600} sx={{ mb: 1.5, fontSize: '15px' }}>
                {billingData.usage.seats.used} / {billingData.usage.seats.total}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={calculatePercentage(
                  billingData.usage.seats.used,
                  billingData.usage.seats.total
                )}
                sx={{
                  height: 6,
                  borderRadius: 1,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 1,
                    backgroundColor: theme.palette.primary.main
                  }
                }}
              />
              <Box sx={{ mt: 1.5 }}>
                <Button 
                  size="small" 
                  variant="outlined" 
                  fullWidth
                  onClick={() => setManageSeatsDialogOpen(true)}
                  sx={{ 
                    fontSize: '13px', 
                    textTransform: 'none',
                    fontWeight: 500,
                    borderColor: theme.palette.divider,
                    color: 'text.primary',
                    '&:hover': {
                      borderColor: theme.palette.divider,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                    }
                  }}
                >
                  Manage seats
                </Button>
              </Box>
            </Paper>

            {/* Storage Card */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: '8px',
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: 'transparent'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <ReceiptIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
                <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '13px' }}>
                  Storage
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600} sx={{ mb: 1.5, fontSize: '15px' }}>
                {billingData.usage.records.used.toLocaleString()} / {billingData.usage.records.total.toLocaleString()} MB
              </Typography>
              <LinearProgress
                variant="determinate"
                value={calculatePercentage(
                  billingData.usage.records.used,
                  billingData.usage.records.total
                )}
                sx={{
                  height: 6,
                  borderRadius: 1,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 1,
                    backgroundColor: theme.palette.info.main
                  }
                }}
              />
              <Box sx={{ mt: 1.5 }}>
                <Button 
                  size="small" 
                  variant="outlined" 
                  fullWidth
                  onClick={() => setStorageDialogOpen(true)}
                  sx={{ 
                    fontSize: '13px', 
                    textTransform: 'none',
                    fontWeight: 500,
                    borderColor: theme.palette.divider,
                    color: 'text.primary',
                    '&:hover': {
                      borderColor: theme.palette.divider,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                    }
                  }}
                >
                  Usage →
                </Button>
              </Box>
            </Paper>

            {/* AI Credits Card */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: '8px',
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: 'transparent'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <InfoIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '13px' }}>
                  AI credits
                </Typography>
                <Tooltip title="AI credits are used for automated processing including transcription, AI analysis, document processing, and chat assistance. Credits reset monthly based on your plan.">
                  <InfoIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Typography variant="body1" fontWeight={600} sx={{ mb: 1.5, fontSize: '15px' }}>
                {billingData.usage.automationCredits.used.toLocaleString()} / {billingData.usage.automationCredits.total.toLocaleString()}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={calculatePercentage(
                  billingData.usage.automationCredits.used,
                  billingData.usage.automationCredits.total
                )}
                sx={{
                  height: 6,
                  borderRadius: 1,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 1,
                    backgroundColor: theme.palette.warning.main
                  }
                }}
              />
              <Box sx={{ mt: 1.5 }}>
                <Button 
                  size="small" 
                  variant="outlined" 
                  fullWidth
                  onClick={() => setTokenUsageDialogOpen(true)}
                  sx={{ 
                    fontSize: '13px', 
                    textTransform: 'none',
                    fontWeight: 500,
                    borderColor: theme.palette.divider,
                    color: 'text.primary',
                    '&:hover': {
                      borderColor: theme.palette.divider,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                    }
                  }}
                >
                  Usage →
                </Button>
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Billing Details Section */}
        <Box sx={{ mb: 4, textAlign: 'left' }}>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ fontSize: '16px', textAlign: 'left' }}>
            Billing details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '14px', textAlign: 'left' }}>
            Manage your payment methods and billing information.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: 2
            }}
          >
            {/* Address Card */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: '8px',
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: 'transparent'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '13px' }}>
                  Address
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={handleEditAddress}
                  sx={{ 
                    ml: 1,
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'
                    }
                  }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '12px' }}>
                Update your billing address
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', mb: 0.5 }}>Email</Typography>
                  <Typography variant="body2" sx={{ fontSize: '13px' }}>{billingData.billingAddress.email}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', mb: 0.5 }}>Company name</Typography>
                  <Typography variant="body2" sx={{ fontSize: '13px' }}>{billingData.billingAddress.companyName}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', mb: 0.5 }}>Address</Typography>
                  <Typography variant="body2" sx={{ fontSize: '13px', lineHeight: 1.6 }}>
                    {billingData.billingAddress.address}<br />
                    {billingData.billingAddress.city}<br />
                    {billingData.billingAddress.state}<br />
                    {billingData.billingAddress.zipCode}<br />
                    {billingData.billingAddress.country}
                  </Typography>
                </Box>
                {billingData.billingAddress.einNumber && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', mb: 0.5 }}>EIN number</Typography>
                    <Typography variant="body2" sx={{ fontSize: '13px' }}>{billingData.billingAddress.einNumber}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Payment Methods Card */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: '8px',
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: 'transparent'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '13px' }}>
                  Payment
                </Typography>
                <IconButton 
                  size="small"
                  onClick={() => setAddPaymentDialogOpen(true)}
                  sx={{ 
                    ml: 1,
                    color: 'text.secondary',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'
                    }
                  }}
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '12px' }}>
                Manage your payment methods
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {billingData.paymentMethods.map((method) => (
                  <Box
                    key={method.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5
                    }}
                  >
                    <CreditCardIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500 }}>
                        {method.brand} • {method.last4}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px' }}>
                        Exp {method.expiryMonth}/{method.expiryYear}
                      </Typography>
                    </Box>
                    {method.isPrimary && (
                      <Chip
                        label="Primary"
                        size="small"
                        color="success"
                        sx={{ height: 22, fontSize: '11px', fontWeight: 500 }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Invoice History */}
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ fontSize: '16px', textAlign: 'left' }}>
            History
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '14px', textAlign: 'left' }}>
            View and track your past invoices and payment history
          </Typography>

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: '8px',
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: 'transparent'
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                  <TableCell sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>Reference</TableCell>
                  <TableCell sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>Total incl. tax</TableCell>
                  <TableCell sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>Date</TableCell>
                  <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontSize: '12px', fontWeight: 600, color: 'text.secondary' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billingData.invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell sx={{ fontSize: '13px' }}>{invoice.reference}</TableCell>
                    <TableCell sx={{ fontSize: '13px' }}>
                      ${invoice.amount.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px' }}>
                      {format(new Date(invoice.date), 'do MMM yyyy')}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        size="small"
                        color={
                          invoice.status === 'paid'
                            ? 'success'
                            : invoice.status === 'pending'
                            ? 'warning'
                            : 'error'
                        }
                        sx={{ height: 24, fontSize: '12px' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadInvoice(invoice)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Edit Address Dialog */}
        <Dialog
          open={isAddressDialogOpen}
          onClose={() => setIsAddressDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '12px',
              boxShadow: theme.palette.mode === 'dark' 
                ? '0 8px 32px rgba(0, 0, 0, 0.6)' 
                : '0 8px 32px rgba(0, 0, 0, 0.12)'
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            pb: 2,
            px: 3,
            pt: 2.5,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: '16px' }}>
              Edit Billing Address
            </Typography>
            <IconButton
              onClick={() => setIsAddressDialogOpen(false)}
              size="small"
              sx={{ color: 'text.secondary', ml: 2 }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.75, fontSize: '13px', color: 'text.primary' }}>
                  Email
                </Typography>
                <TextField
                  fullWidth
                  placeholder="billing@example.com"
                  value={editedAddress?.email || ''}
                  onChange={(e) =>
                    setEditedAddress(
                      editedAddress
                        ? { ...editedAddress, email: e.target.value }
                        : null
                    )
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '13px',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'
                    }
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.75, fontSize: '13px', color: 'text.primary' }}>
                  Company Name
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Example Company"
                  value={editedAddress?.companyName || ''}
                  onChange={(e) =>
                    setEditedAddress(
                      editedAddress
                        ? { ...editedAddress, companyName: e.target.value }
                        : null
                    )
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '13px',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'
                    }
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.75, fontSize: '13px', color: 'text.primary' }}>
                  Address
                </Typography>
                <TextField
                  fullWidth
                  placeholder="123 Main Street"
                  value={editedAddress?.address || ''}
                  onChange={(e) =>
                    setEditedAddress(
                      editedAddress
                        ? { ...editedAddress, address: e.target.value }
                        : null
                    )
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '13px',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'
                    }
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.75, fontSize: '13px', color: 'text.primary' }}>
                  City
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Springfield"
                  value={editedAddress?.city || ''}
                  onChange={(e) =>
                    setEditedAddress(
                      editedAddress
                        ? { ...editedAddress, city: e.target.value }
                        : null
                    )
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '13px',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'
                    }
                  }}
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.75, fontSize: '13px', color: 'text.primary' }}>
                    State
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="IL"
                    value={editedAddress?.state || ''}
                    onChange={(e) =>
                      setEditedAddress(
                        editedAddress
                          ? { ...editedAddress, state: e.target.value }
                          : null
                      )
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '13px',
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'
                      }
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.75, fontSize: '13px', color: 'text.primary' }}>
                    Zip Code
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="62701"
                    value={editedAddress?.zipCode || ''}
                    onChange={(e) =>
                      setEditedAddress(
                        editedAddress
                          ? { ...editedAddress, zipCode: e.target.value }
                          : null
                      )
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '13px',
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'
                      }
                    }}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.75, fontSize: '13px', color: 'text.primary' }}>
                  Country
                </Typography>
                <TextField
                  fullWidth
                  placeholder="United States of America"
                  value={editedAddress?.country || ''}
                  onChange={(e) =>
                    setEditedAddress(
                      editedAddress
                        ? { ...editedAddress, country: e.target.value }
                        : null
                    )
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '13px',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'
                    }
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.75, fontSize: '13px', color: 'text.primary' }}>
                  EIN Number (optional)
                </Typography>
                <TextField
                  fullWidth
                  placeholder="12-3456789"
                  value={editedAddress?.einNumber || ''}
                  onChange={(e) =>
                    setEditedAddress(
                      editedAddress
                        ? { ...editedAddress, einNumber: e.target.value }
                        : null
                    )
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '13px',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'
                    }
                  }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 2 }}>
            <Button 
              onClick={() => setIsAddressDialogOpen(false)}
              sx={{ 
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 500,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'transparent'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAddress}
              variant="contained"
              disabled={isSavingAddress}
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                fontWeight: 600,
                borderRadius: '6px',
                px: 2.5,
                backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
                }
              }}
            >
              {isSavingAddress ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Support Chat */}
        <SupportChat open={chatOpen} onClose={() => setChatOpen(false)} />

        {/* Manage Seats Dialog */}
        {billingData && (
          <ManageSeatsDialog
            open={manageSeatsDialogOpen}
            onClose={() => setManageSeatsDialogOpen(false)}
            currentSeats={billingData.usage.seats.used}
            pricePerSeat={billingData.plan.price}
            onConfirm={handleConfirmSeats}
          />
        )}

        {/* Storage Usage Dialog */}
        {billingData && (
          <StorageUsageDialog
            open={storageDialogOpen}
            onClose={() => setStorageDialogOpen(false)}
            usedStorage={billingData.usage.records.used}
            totalStorage={billingData.usage.records.total}
            onUpgrade={handleUpgradePlan}
          />
        )}

        {/* Token Usage Dialog */}
        {billingData && (
          <TokenUsageDialog
            open={tokenUsageDialogOpen}
            onClose={() => setTokenUsageDialogOpen(false)}
            usedTokens={billingData.usage.automationCredits.used}
            totalTokens={billingData.usage.automationCredits.total}
            onUpgrade={handleUpgradePlan}
          />
        )}

        {/* Add Payment Method Dialog */}
        <AddPaymentDialog
          open={addPaymentDialogOpen}
          onClose={() => setAddPaymentDialogOpen(false)}
          onSubmit={handleAddPaymentMethod}
        />
      </Box>
    </Box>
  );
};

